import React, { useState, useEffect } from "react";
import { Form, message, Modal } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import {
  useRawPurchasesQuery,
  useCreateRawPurchaseMutation,
  useUpdateRawPurchaseMutation,
  useDeleteRawPurchaseMutation,
  useRawMaterialsQuery,
  useSuppliersQuery,
  useSectionAccess,
} from "../../Services";
import { PurchaseStatus, PurchasePaymentStatus, RawPurchaseAction } from "../../Types";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { PageHeader } from "../../components";
import {
  RawMaterial,
  RawMaterialPurchase,
  RawMaterialPurchaseItem,
} from "../../Types";
import {
  RawPurchasesFilters,
  RawPurchaseFormDrawer,
  RawPurchasesTable,
} from "./common";

export const RawPurchasesPage = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] =
    useState<RawMaterialPurchase | null>(null);
  const [form] = Form.useForm();

  const [purchaseItems, setPurchaseItems] = useState<RawMaterialPurchaseItem[]>(
    [],
  );
  const [globalDiscount, setGlobalDiscount] = useState<number>(0);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [materialSearchText, setMaterialSearchText] = useState("");

  // ─── Data Queries ──────────────────────────────────────────────────────────

  const { data: suppliers = [] } = useSuppliersQuery();

  const { data: rawMaterialsRes } = useRawMaterialsQuery(
    { page: 1, limit: 100 },
    { staleTime: 5 * 60 * 1000 },
  );
  const allRawMaterials = rawMaterialsRes?.data || [];

  const {
    data: purchases,
    isLoading,
    refetch,
  } = useRawPurchasesQuery(
    { page, limit: PAGE_SIZE, search, supplier_id: supplierId, status },
    { placeholderData: (prev) => prev },
  );

  // const purchases: RawMaterialPurchase[] = purchasesData?.purchases ?? [];
  const total: number = purchases?.total ?? 0;

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const createMutation = useCreateRawPurchaseMutation();
  const updateMutation = useUpdateRawPurchaseMutation();
  const deleteMutation = useDeleteRawPurchaseMutation();

  const { isAllowed } = useSectionAccess("raw_purchases");

  // ─── Derived Totals ────────────────────────────────────────────────────────

  const grossTotal = purchaseItems.reduce((acc, curr) => acc + curr.total, 0);
  const grandTotal = Math.max(0, grossTotal - globalDiscount);
  const dueAmount = Math.max(0, grandTotal - paidAmount);
  const calculatedPaymentStatus: PurchasePaymentStatus =
    dueAmount <= 0
      ? PurchasePaymentStatus.PAID
      : paidAmount > 0
        ? PurchasePaymentStatus.PARTIAL
        : PurchasePaymentStatus.DUE;

  useEffect(() => {
    form.setFieldsValue({ payment_status: calculatedPaymentStatus });
  }, [calculatedPaymentStatus, form]);

  // ─── Drawer Handlers ───────────────────────────────────────────────────────

  const openDrawer = (purchase?: RawMaterialPurchase) => {
    if (purchase) {
      setEditingPurchase(purchase);
      form.setFieldsValue({
        supplier_id: purchase.supplier_id,
        purchase_date: dayjs(purchase.purchase_date),
        invoice_no: purchase.invoice_no,
        po_no: purchase.po_no,
        payment_status: purchase.payment_status,
        due_date: purchase.due_date ? dayjs(purchase.due_date) : null,
        status: purchase.status,
      });
      setPurchaseItems(purchase.items || []);
      setGlobalDiscount(purchase.discount_amount);
      setPaidAmount(purchase.paid_amount);
    } else {
      setEditingPurchase(null);
      form.resetFields();
      form.setFieldsValue({
        purchase_date: dayjs(),
        status: PurchaseStatus.DRAFT,
        payment_status: PurchasePaymentStatus.DUE,
      });
      setPurchaseItems([]);
      setGlobalDiscount(0);
      setPaidAmount(0);
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingPurchase(null);
    form.resetFields();
    setPurchaseItems([]);
    setGlobalDiscount(0);
    setPaidAmount(0);
  };

  const handleAddMaterialItem = (materialId: string) => {
    const mat = allRawMaterials.find((rm) => rm.id === materialId);
    if (!mat) return;

    const defaultStock = mat.stocks?.find((s: any) => s.is_default === 1);
    const costPrice = defaultStock ? defaultStock.cost_price : 0;

    if (purchaseItems.some((itm) => itm.raw_material_id === materialId)) {
      message.warning(`"${mat.name}" is already added to the purchase list.`);
      return;
    }

    const newItem: RawMaterialPurchaseItem = {
      raw_material_id: mat.id,
      raw_material_name: mat.name,
      raw_material_code: mat.code,
      raw_material_unit: mat.unit,
      cost_price: costPrice,
      quantity: 1,
      discount_percent: 0,
      total: costPrice,
      batch_no: "BATCH-new",
      expiry_date: null,
    };

    setPurchaseItems((prev) => [...prev, newItem]);
    setMaterialSearchText("");
  };

  const handleUpdateItem = (
    index: number,
    key: keyof RawMaterialPurchaseItem,
    val: any,
  ) => {
    setPurchaseItems((prev) => {
      const updated = [...prev];
      const itm = { ...updated[index], [key]: val };

      if (
        key === "cost_price" ||
        key === "quantity" ||
        key === "discount_percent"
      ) {
        const netCost = itm.cost_price * (1 - itm.discount_percent / 100);
        itm.total = netCost * itm.quantity;
      }

      updated[index] = itm;
      return updated;
    });
  };

  const handleRemoveItem = (index: number) => {
    setPurchaseItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async (submitStatus: PurchaseStatus) => {
    try {
      const values = await form.validateFields();

      if (purchaseItems.length === 0) {
        message.error("At least one purchase item is required.");
        return;
      }

      for (let i = 0; i < purchaseItems.length; i++) {
        const item = purchaseItems[i];
        if (item.cost_price <= 0) {
          message.error(`Unit Cost in item #${i + 1} must be greater than 0.`);
          return;
        }
        if (item.quantity <= 0) {
          message.error(`Quantity in item #${i + 1} must be greater than 0.`);
          return;
        }
      }

      const payload = {
        ...values,
        purchase_date: values.purchase_date.format("YYYY-MM-DD"),
        due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
        discount_amount: globalDiscount,
        paid_amount: paidAmount,
        status: submitStatus,
        items: purchaseItems,
      };

      if (editingPurchase) {
        const res = await updateMutation.mutateAsync({
          id: editingPurchase.id,
          ...payload,
        });
        if (res?.success) {
          message.success("Purchase updated successfully.");
          closeDrawer();
        } else {
          message.error(res?.message || "Failed to update purchase.");
        }
      } else {
        const res = await createMutation.mutateAsync(payload);
        if (res?.success) {
          message.success("Purchase recorded successfully.");
          closeDrawer();
        } else {
          message.error(res?.message || "Failed to save purchase.");
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmComplete = () => {
    Modal.confirm({
      title: "Complete Purchase Transaction?",
      content:
        "Completing this purchase will permanently lock it, register the raw stock, and increase their stock batch levels. This action cannot be reversed.",
      okText: "Complete Purchase",
      okButtonProps: { type: "primary" },
      cancelText: "Cancel",
      onOk: () => handleFormSubmit(PurchaseStatus.COMPLETED),
    });
  };

  const handleDelete = (rec: RawMaterialPurchase) => {
    deleteMutation.mutate(rec.id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Purchase draft deleted successfully.");
        } else {
          message.error(res?.message || "Failed to delete draft.");
        }
      },
    });
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Raw Purchases"
        subText={`${total} invoices`}
        handleButtons={[
          ...(isAllowed(RawPurchaseAction.Create)
            ? [
                {
                  title: "Add Purchase",
                  icon: PlusOutlined,
                  onClick: () => openDrawer(),
                },
              ]
            : []),
          { title: "", icon: ReloadOutlined, onClick: refetch },
        ]}
        searchBar={
          <RawPurchasesFilters
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            supplierId={supplierId}
            onSupplierChange={(v) => {
              setSupplierId(v);
              setPage(1);
            }}
            status={status}
            onStatusChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            suppliers={suppliers}
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <RawPurchasesTable
          purchases={purchases}
          total={total}
          page={page}
          isLoading={isLoading}
          onPageChange={setPage}
          onOpen={openDrawer}
          onDelete={handleDelete}
        />
      </div>

      <RawPurchaseFormDrawer
        open={drawerOpen}
        editingPurchase={editingPurchase}
        form={form}
        suppliers={suppliers}
        allRawMaterials={allRawMaterials}
        materialSearchText={materialSearchText}
        purchaseItems={purchaseItems}
        globalDiscount={globalDiscount}
        paidAmount={paidAmount}
        grossTotal={grossTotal}
        grandTotal={grandTotal}
        dueAmount={dueAmount}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        onClose={closeDrawer}
        onSaveDraft={() => handleFormSubmit(PurchaseStatus.DRAFT)}
        onComplete={handleConfirmComplete}
        onMaterialSearchChange={setMaterialSearchText}
        onAddMaterial={handleAddMaterialItem}
        onUpdateItem={handleUpdateItem}
        onRemoveItem={handleRemoveItem}
        onDiscountChange={setGlobalDiscount}
        onPaidChange={setPaidAmount}
      />
    </div>
  );
};
